import styled from 'styled-components';
import {
  TARIFF_CARD_GAP_PX,
  TARIFF_CARDS_SCROLL_PADDING_X,
} from './BusinessTariffs.constants';
import { Typography } from '@/components/UI/Typography/Typography.styles';

export const TariffsSection = styled.section`
  margin-top: 160px;
  margin-left: auto;
  margin-right: auto;
  max-width: 1280px;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    padding: 0 40px;
    margin-top: 140px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 0;
  }
`;

export const TariffsTitle = styled(Typography.Header)`
  text-align: center;
  margin-bottom: 60px;
  font-size: 34px;
  font-weight: 700;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 26px;
    margin-bottom: 40px;
  }
`;

export const TariffCardsScrollWrapper = styled.div`
  overflow: hidden;
  margin-left: -50px;
  margin-right: -50px;
  width: calc(100% + 100px);
  padding-left: ${TARIFF_CARDS_SCROLL_PADDING_X}px;
  padding-right: ${TARIFF_CARDS_SCROLL_PADDING_X}px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    margin-left: -15px;
    margin-right: -15px;
    width: calc(100% + 30px);
    padding-left: 15px;
    padding-right: 15px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    margin-left: -15px;
    margin-right: -15px;
    width: calc(100% + 30px);
    padding-left: 15px;
    padding-right: 15px;
  }
`;

export const TariffCardsTrack = styled.div<{
  $trackWidthPx: number;
  $translatePx: number;
  $cardWidthPx: number;
}>`
  display: flex;
  gap: ${TARIFF_CARD_GAP_PX}px;
  width: ${({ $trackWidthPx }) => $trackWidthPx}px;
  transform: translateX(${({ $translatePx }) => $translatePx}px);
  transition: transform 0.3s ease;

  & > * {
    flex: 0 0 ${({ $cardWidthPx }) => $cardWidthPx}px;
    box-sizing: border-box;
  }
`;

export const TariffCardSnap = styled.div`
  box-sizing: border-box;
  min-width: 0;
  padding: 0 4px;
`;

export const TariffInfo = styled.div`
  background: ${({ theme }) => theme.palette.primary.background};
  border-radius: 28px;
`;

export const TariffsContainer = styled.div`
  display: flex;
  justify-content: space-between;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
    align-items: center;
  }
`;

export const TariffsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  max-width: 400px;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    max-width: 250px;
    margin-right: 50px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: row;
    overflow-x: auto;
    min-width: 100%;
    margin-bottom: 40px;
    padding-left: 20px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding-left: 40px;
  }
`;

export const TariffItem = styled.div<{ $active?: boolean }>`
  padding: 20px 40px;
  border-radius: 20px;
  background: ${({ $active, theme }) =>
    $active
      ? theme.palette.secondary.background
      : theme.palette.primary.background};
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 24px;
  font-weight: 700;
  flex-shrink: 0;
  white-space: nowrap;
  color: ${({ theme }) => theme.palette.primary.labels};
  opacity: ${({ $active }) => ($active ? '1' : '0.4')};
  max-height: 70px;

  &:hover {
    background: ${({ theme }) => theme.palette.secondary.background};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    min-width: fit-content;
    padding: 20px;
    font-size: 18px;
    border-radius: 50px;
    height: 55px;
  }
`;

export const TariffArrow = styled.span`
  width: 36px;
  height: 36px;
  background-image: url('/images/business/arrow-right.svg');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
`;

export const TariffDetails = styled.div`
  flex: 1;
  border-radius: 28px;
  background: ${({ theme }) => theme.palette.secondary.background};
  padding: 50px;
  display: flex;
  flex-direction: column;
  max-width: 730px;
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    max-width: 100%;
    width: 100%;
    padding: 20px 15px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 15px;
  }
`;

export const TariffHeader = styled.div`
  margin-bottom: 25px;
  padding: 25px 25px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.primary.border};
`;

export const TariffName = styled.div`
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 15px;
  color: ${({ theme }) => theme.palette.primary.labels};
`;

export const TariffPrice = styled.div`
  font-size: 26px;
  font-weight: 700;
  color: ${({ theme }) => theme.palette.primary.labels};
`;

export const TariffFeatures = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  padding: 0 25px 25px;
  flex: 1;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

export const TariffFeature = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
`;

export const TariffFeatureIcon = styled.div<{
  $type: 'wallet' | 'ads' | 'discount';
}>`
  width: 48px;
  height: 48px;
  min-width: 48px;
  border-radius: 12px;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  background-image: ${({ $type }) => {
    switch ($type) {
      case 'wallet':
        return "url('/images/business/tariff-wallet.png')";
      case 'ads':
        return "url('/images/business/tariff-paidads.png')";
      case 'discount':
        return "url('/images/business/tariff-discount.png')";
      default:
        return 'none';
    }
  }};
`;

export const TariffFeatureTitle = styled.div`
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 5px;
  color: ${({ theme }) => theme.palette.primary.labels};
`;

export const TariffFeatureDesc = styled.div`
  font-size: 15px;
  color: ${({ theme }) => theme.palette.primary.labels};
`;

export const TariffDurationsSection = styled.div`
  width: 100%;
  margin-top: 15px;
  padding: 5px;
  min-height: 52px;
  border-radius: 50px;
  background: ${({ theme }) => theme.palette.primary.background};
  box-shadow: 4px 4px 15px 0 rgba(0, 0, 0, 0.04);
  overflow: hidden;
  box-sizing: border-box;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 4px;
    min-height: 44px;
  }
`;

export const TariffDurations = styled.div`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-wrap: nowrap;
    gap: 4px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.xsm}) {
    gap: 4px;
  }
`;

export const TariffDurationSlider = styled.div<{
  $left: number;
  $width: number;
}>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${({ $left }) => $left}px;
  width: ${({ $width }) => $width}px;
  border-radius: 50px;
  background: ${({ theme }) => theme.palette.brand.main};
  transition: left 0.25s ease-out, width 0.25s ease-out;
  z-index: 0;
  pointer-events: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.xsm}) {
    max-width: 75px;
  }
`;

export const TariffDurationButton = styled.button<{ $active?: boolean }>`
  position: relative;
  z-index: 1;
  padding: 12px 24px;
  border-radius: 50px;
  border: 1px solid transparent;
  background: transparent;
  color: ${({ $active, theme }) =>
    $active ? theme.palette.system.white : theme.palette.primary.labels};
  font-size: 15px;
  cursor: pointer;
  white-space: nowrap;
  flex: 1;
  min-width: min-content;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 12px 6px;
    font-size: 15px;
    flex: 1 1 0;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.xsm}) {
    padding: 12px 4px;
  }
`;
